import { useEffect, useState } from "react";
import {
  type NativeProcessStatsSnapshot,
  nativeProcessStats,
} from "@-/dev.improve.haniverse.modules.process-stats";
import { deriveProcessStatsSample } from "#derive-process-stats-sample";
import { readJsHeapStats } from "#js-runtime-stats";

export type ProcessStatsSample = {
  /** Process CPU over the last interval as a percentage of one core; `null` until two readings exist. */
  cpuPercent: number | null;
  jsHeapBytes: number | null;
  mainThreadLatencyMs: number | null;
  memoryFootprintBytes: number | null;
  thermalState: string | null;
};

const EMPTY: ProcessStatsSample = {
  cpuPercent: null,
  jsHeapBytes: null,
  mainThreadLatencyMs: null,
  memoryFootprintBytes: null,
  thermalState: null,
};

/**
 * A lightweight live readout for the performance overlay: polls the native counters every
 * `intervalMs` while `enabled` and derives CPU from consecutive readings. Independent of the
 * recorder so the overlay works with recording off.
 */
export function useProcessStatsSample(enabled: boolean, intervalMs = 1000): ProcessStatsSample {
  const [sample, setSample] = useState<ProcessStatsSample>(EMPTY);

  useEffect(() => {
    if (!enabled) {
      setSample(EMPTY);
      return;
    }
    let previous: NativeProcessStatsSnapshot | null = null;
    const read = (): void => {
      let snapshot: NativeProcessStatsSnapshot | null = null;
      try {
        snapshot = nativeProcessStats?.getSnapshot(0) ?? null;
      } catch {
        snapshot = null;
      }
      const derived = snapshot === null ? null : deriveProcessStatsSample(snapshot, previous);
      previous = snapshot;
      setSample({
        cpuPercent: derived?.cpuPercent ?? null,
        jsHeapBytes: readJsHeapStats()?.allocatedBytes ?? null,
        mainThreadLatencyMs: derived?.mainThreadLatencyMs ?? null,
        memoryFootprintBytes: derived?.memoryFootprintBytes ?? null,
        thermalState: derived?.thermalState ?? null,
      });
    };
    read();
    const timer = setInterval(read, intervalMs);
    return () => {
      clearInterval(timer);
    };
  }, [enabled, intervalMs]);

  return sample;
}
